import { useEffect, useRef } from 'react'

import { reportTicketRead } from '../readReceipts'
import type { SupportMessage, SupportTicket } from '../types'

interface ThreadRead {
  ticketPublicId: string | null
  seenUpTo: string | null
}

const newestStaffMessageAt = (messages: SupportMessage[]): string | null =>
  messages.reduce<string | null>(
    (newest, message) =>
      message.authorType === 'staff' && (!newest || message.createdAt > newest)
        ? message.createdAt
        : newest,
    null,
  )

// How much of the thread the user has now seen. Every ticket is created with
// its opening message, so an empty list means the thread has not loaded yet and
// there is nothing to call read.
export const readWatermark = (
  ticket: SupportTicket | null,
  messages: SupportMessage[],
): string | null => {
  if (!ticket || messages.length === 0) return null
  // createdAt never moves, so a thread nobody has answered yet reports once per
  // page load instead of once per message the user sends.
  return newestStaffMessageAt(messages) ?? ticket.createdAt
}

const reportWhenVisible = ({ ticketPublicId, seenUpTo }: ThreadRead) => {
  if (!ticketPublicId || !seenUpTo) return
  if (document.visibilityState !== 'visible') return
  reportTicketRead(ticketPublicId, seenUpTo)
}

// Tells the support service the thread was read: when it opens, and again
// whenever a staff reply lands while it is on screen. A reply that arrives with
// the tab hidden is only reported once the tab is looked at again.
export const useReportTicketRead = (
  ticket: SupportTicket | null,
  messages: SupportMessage[],
) => {
  const ticketPublicId = ticket?.publicId ?? null
  const seenUpTo = readWatermark(ticket, messages)

  // The tab listener is mounted once and reads this, so a new staff reply does
  // not tear the listener down and add it back. Written inside the effect so a
  // discarded render never leaves its values behind.
  const threadRead = useRef<ThreadRead>({ ticketPublicId, seenUpTo })

  useEffect(() => {
    threadRead.current = { ticketPublicId, seenUpTo }
    reportWhenVisible({ ticketPublicId, seenUpTo })
  }, [ticketPublicId, seenUpTo])

  useEffect(() => {
    const report = () => reportWhenVisible(threadRead.current)
    document.addEventListener('visibilitychange', report)
    return () => document.removeEventListener('visibilitychange', report)
  }, [])
}
